import React, { createContext, useContext, useEffect, useMemo, useRef, useState, useCallback } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { Phone, PhoneOff, Video, VideoOff, Mic, MicOff, PhoneIncoming, ScreenShare, ScreenShareOff, Maximize, Minimize, Users } from "lucide-react";

const CallCtx = createContext(null);
export const useCall = () => useContext(CallCtx);

const ICE = { iceServers: [{ urls: "stun:stun.l.google.com:19302" }, { urls: "stun:stun1.l.google.com:19302" }] };
const initials = (n = "") => n.split(" ").map((s) => s[0]).slice(0, 2).join("").toUpperCase();

const Stream = ({ stream, muted, className }) => {
  const ref = useRef(null);
  useEffect(() => { if (ref.current) ref.current.srcObject = stream || null; }, [stream]);
  return <video ref={ref} autoPlay playsInline muted={muted} className={className} />;
};

const stateDot = (s) => s === "connected" || s === "completed" ? "bg-emerald-400" : (s === "failed" || s === "disconnected" || s === "closed") ? "bg-red-500" : "bg-amber-400";
const parseCandType = (c) => { const m = /typ (\w+)/.exec(c || ""); return m ? m[1] : null; };

const AudioSink = ({ stream }) => {
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.srcObject = stream || null;
    const tryPlay = () => { const p = el.play?.(); if (p && p.catch) p.catch(() => {}); };
    tryPlay();
    // Autoplay-with-sound can be blocked if the user's gesture went stale during ringing;
    // retry playback on any user interaction so remote audio always starts.
    const onGesture = () => tryPlay();
    document.addEventListener("pointerdown", onGesture, { passive: true });
    document.addEventListener("click", onGesture);
    return () => {
      document.removeEventListener("pointerdown", onGesture);
      document.removeEventListener("click", onGesture);
    };
  }, [stream]);
  return <audio ref={ref} autoPlay playsInline />;
};

const RemoteTile = ({ stream, videoOn, name, speaking, state }) => (
  <div className={`relative h-full w-full overflow-hidden rounded-lg bg-black transition-shadow ${speaking ? "ring-2 ring-emerald-400" : ""}`} data-testid="remote-tile" data-speaking={speaking ? "true" : "false"} data-conn={state || ""}>
    <AudioSink stream={stream} />
    <Stream stream={stream} muted className="absolute inset-0 h-full w-full object-contain" />
    {!videoOn && (
      <div className="absolute inset-0 flex flex-col items-center justify-center gap-1 bg-navy">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-base font-bold text-gold">{initials(name)}</div>
      </div>
    )}
    <span className="absolute bottom-1 left-1 flex max-w-[95%] items-center gap-1 truncate rounded bg-black/50 px-1.5 py-0.5 text-[10px] text-white">
      <span className={`h-1.5 w-1.5 shrink-0 rounded-full ${stateDot(state)}`} />
      {name}{state && state !== "connected" && state !== "completed" ? ` · ${state}` : ""}
    </span>
  </div>
);

export const CallProvider = ({ children }) => {
  const { user } = useAuth();
  const roomRef = useRef(null);       // {room_id, group_id, group_name, media}
  const localRef = useRef(null);      // MediaStream (mic/cam)
  const peersRef = useRef({});        // {peerId: {pc, name}}
  const pendingCandRef = useRef({});  // {peerId: [candidate]}
  const seqRef = useRef(0);
  const pollRef = useRef(null);
  const screenRef = useRef(null);
  const camTrackRef = useRef(null);
  const overlayRef = useRef(null);
  const incomingRef = useRef(null);
  const connectingRef = useRef(false);
  const remoteStreamsRef = useRef({});
  const iceRef = useRef(ICE);

  const [room, setRoom] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({}); // {peerId: {name, stream, videoOn}}
  const [incoming, setIncomingState] = useState(null);
  const [localStream, setLocalStream] = useState(null);
  const [muted, setMuted] = useState(false);
  const [camOff, setCamOff] = useState(false);
  const [sharing, setSharing] = useState(false);
  const [screenStream, setScreenStream] = useState(null);
  const [isFs, setIsFs] = useState(false);
  const [secs, setSecs] = useState(0);
  const [speakingIds, setSpeakingIds] = useState({});
  const [peerStates, setPeerStates] = useState({});
  const [candTypes, setCandTypes] = useState({});
  const [turnCfg, setTurnCfg] = useState(null);
  const [localSpeaking, setLocalSpeaking] = useState(false);

  const setIncoming = (v) => { incomingRef.current = v; setIncomingState(v); };

  const refreshIce = async () => {
    try {
      const { data } = await api.get("/calls/ice");
      if (data?.iceServers?.length) {
        iceRef.current = { iceServers: data.iceServers };
        setTurnCfg(data.iceServers.filter((s) => String(s.urls || "").startsWith("turn")).length);
      }
    } catch (e) { setTurnCfg(-1); }
  };

  // ---- signaling helpers (close over refs / stable setters only) ----
  const signal = (toId, kind, data) => {
    const rid = roomRef.current?.room_id;
    if (!rid) return;
    api.post(`/calls/rooms/${rid}/signal`, { to_id: toId, kind, data }).catch(() => {});
  };

  const videoSenderOf = (pc) => {
    const tr = pc.getTransceivers().find(
      (t) => (t.sender && t.sender.track && t.sender.track.kind === "video") ||
             (t.receiver && t.receiver.track && t.receiver.track.kind === "video")
    );
    return tr ? tr.sender : null;
  };

  const makePeer = (peerId, name, initiator) => {
    const pc = new RTCPeerConnection(iceRef.current);
    if (localRef.current) {
      localRef.current.getTracks().forEach((t) => pc.addTrack(t, localRef.current));
      // Only the offerer adds a spare video line (for audio calls) so screen-share works;
      // the answerer gets its video transceiver from the remote offer (avoids m-line desync / one-way media).
      if (initiator && localRef.current.getVideoTracks().length === 0) {
        try { pc.addTransceiver("video", { direction: "sendrecv" }); } catch (e) {}
      }
    }
    // if already sharing screen, send screen to the new peer instead of camera
    if (screenRef.current) {
      const st = screenRef.current.getVideoTracks()[0];
      const s = videoSenderOf(pc);
      if (st && s) s.replaceTrack(st).catch(() => {});
    }
    pc.onicecandidate = (e) => {
      if (!e.candidate) return;
      signal(peerId, "candidate", e.candidate.toJSON());
      const t = e.candidate.type || parseCandType(e.candidate.candidate);
      if (t) setCandTypes((p) => (p[t] ? p : { ...p, [t]: true }));
    };
    pc.onconnectionstatechange = () => setPeerStates((p) => ({ ...p, [peerId]: pc.connectionState }));
    pc.oniceconnectionstatechange = () => { if (pc.iceConnectionState === "failed") { try { pc.restartIce?.(); } catch (e) {} } };
    pc.ontrack = (e) => {
      const stream = e.streams[0];
      setRemoteStreams((prev) => ({ ...prev, [peerId]: { name, stream, videoOn: prev[peerId]?.videoOn || false } }));
      if (e.track && e.track.kind === "video") {
        const t = e.track;
        const upd = () => setRemoteStreams((prev) => (prev[peerId] ? { ...prev, [peerId]: { ...prev[peerId], videoOn: !t.muted } } : prev));
        upd(); t.onmute = upd; t.onunmute = upd;
      }
    };
    peersRef.current[peerId] = { pc, name };
    return pc;
  };

  const offerTo = async (peerId, name) => {
    const pc = makePeer(peerId, name, true);
    const offer = await pc.createOffer();
    await pc.setLocalDescription(offer);
    signal(peerId, "offer", { type: offer.type, sdp: offer.sdp });
  };

  const handleSignal = async (sig) => {
    const from = sig.from_id;
    try {
      if (sig.kind === "offer") {
        const entry = peersRef.current[from];
        const pc = entry ? entry.pc : makePeer(from, sig.from_name || "Guest", false);
        await pc.setRemoteDescription(new RTCSessionDescription(sig.data));
        for (const c of (pendingCandRef.current[from] || [])) { try { await pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) {} }
        pendingCandRef.current[from] = [];
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);
        signal(from, "answer", { type: answer.type, sdp: answer.sdp });
      } else if (sig.kind === "answer") {
        const entry = peersRef.current[from];
        if (entry) {
          await entry.pc.setRemoteDescription(new RTCSessionDescription(sig.data));
          for (const c of (pendingCandRef.current[from] || [])) { try { await entry.pc.addIceCandidate(new RTCIceCandidate(c)); } catch (e) {} }
          pendingCandRef.current[from] = [];
        }
      } else if (sig.kind === "candidate") {
        const entry = peersRef.current[from];
        if (entry && entry.pc.remoteDescription) { try { await entry.pc.addIceCandidate(new RTCIceCandidate(sig.data)); } catch (e) {} }
        else { (pendingCandRef.current[from] = pendingCandRef.current[from] || []).push(sig.data); }
      } else if (sig.kind === "control") {
        if (sig.data?.action === "mute") {
          if (localRef.current) localRef.current.getAudioTracks().forEach((t) => (t.enabled = false));
          setMuted(true);
          toast.info(`You were muted by ${sig.from_name || "the host"}.`);
        }
      }
    } catch (e) {}
  };

  const leaveCleanup = () => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
    Object.values(peersRef.current).forEach((p) => { try { p.pc.close(); } catch (e) {} });
    peersRef.current = {}; pendingCandRef.current = {};
    if (localRef.current) { localRef.current.getTracks().forEach((t) => t.stop()); localRef.current = null; }
    if (screenRef.current) { screenRef.current.getTracks().forEach((t) => t.stop()); screenRef.current = null; }
    camTrackRef.current = null; seqRef.current = 0; roomRef.current = null; connectingRef.current = false;
    if (typeof document !== "undefined" && document.fullscreenElement) { document.exitFullscreen().catch(() => {}); }
    setRoom(null); setRemoteStreams({}); setLocalStream(null); setPeerStates({}); setCandTypes({});
    setMuted(false); setCamOff(false); setSharing(false); setScreenStream(null); setIsFs(false); setSecs(0);
  };

  const startPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const rid = roomRef.current?.room_id;
      if (!rid) return;
      try {
        const { data } = await api.get(`/calls/rooms/${rid}/signals`, { params: { since: seqRef.current } });
        if (data.status === "ended") { toast.info("Call ended."); leaveCleanup(); return; }
        for (const sig of (data.signals || [])) { seqRef.current = Math.max(seqRef.current, sig.seq); await handleSignal(sig); }
        const activeIds = new Set((data.participants || []).map((p) => p.user_id));
        Object.keys(peersRef.current).forEach((pid) => {
          if (!activeIds.has(pid)) {
            try { peersRef.current[pid].pc.close(); } catch (e) {}
            delete peersRef.current[pid];
            setRemoteStreams((prev) => { const n = { ...prev }; delete n[pid]; return n; });
            setPeerStates((prev) => { const n = { ...prev }; delete n[pid]; return n; });
          }
        });
      } catch (e) {}
    }, 900);
  };

  // ---- exposed actions ----
  const connectRoom = useCallback(async (roomId, media) => {
    if (roomRef.current) { toast.error("You're already in a call."); return; }
    connectingRef.current = true;
    await refreshIce();
    let stream;
    try { stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: media === "video" }); }
    catch (e) { connectingRef.current = false; toast.error("Could not access mic/camera."); return; }
    localRef.current = stream; setLocalStream(stream);
    let data;
    try { ({ data } = await api.post(`/calls/rooms/${roomId}/join`)); }
    catch (e) {
      connectingRef.current = false;
      toast.error(e?.response?.data?.detail || "Could not join the call.");
      stream.getTracks().forEach((t) => t.stop()); localRef.current = null; setLocalStream(null); return;
    }
    roomRef.current = { room_id: roomId, group_id: data.group_id, group_name: data.group_name, media: data.media };
    setRoom({ ...roomRef.current }); seqRef.current = 0; connectingRef.current = false;
    startPolling();
    for (const p of (data.peers || [])) { try { await offerTo(p.user_id, p.name); } catch (e) {} }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const startHuddle = useCallback(async (groupId, media = "video") => {
    try {
      const { data } = await api.post("/calls/rooms", { group_id: groupId, media, invite_all: true });
      await connectRoom(data.room_id, media);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the huddle."); }
  }, [connectRoom]);

  const startCall = useCallback(async (groupId, calleeId, calleeName, media = "video") => {
    try {
      const { data } = await api.post("/calls/rooms", { group_id: groupId, media, invite_all: false, invite_ids: [calleeId] });
      await connectRoom(data.room_id, media);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not start the call."); }
  }, [connectRoom]);

  const joinRoom = useCallback(async (roomId, media = "video") => { await connectRoom(roomId, media); }, [connectRoom]);

  const accept = async () => { const inc = incomingRef.current; if (!inc) return; setIncoming(null); await connectRoom(inc.room_id, inc.media); };
  const decline = () => { const inc = incomingRef.current; if (inc) api.post(`/calls/rooms/${inc.room_id}/decline`).catch(() => {}); setIncoming(null); };
  const hangup = () => { const rid = roomRef.current?.room_id; if (rid) api.post(`/calls/rooms/${rid}/leave`).catch(() => {}); leaveCleanup(); };

  const toggleMute = () => {
    if (!localRef.current) return;
    const on = !muted; setMuted(on);
    localRef.current.getAudioTracks().forEach((t) => (t.enabled = !on));
  };
  const toggleCam = () => {
    if (!localRef.current) return;
    const off = !camOff; setCamOff(off);
    localRef.current.getVideoTracks().forEach((t) => (t.enabled = !off));
  };

  const stopShare = () => {
    Object.values(peersRef.current).forEach(({ pc }) => { const s = videoSenderOf(pc); if (s) s.replaceTrack(camTrackRef.current || null).catch(() => {}); });
    if (screenRef.current) { screenRef.current.getTracks().forEach((t) => t.stop()); screenRef.current = null; }
    camTrackRef.current = null; setScreenStream(null); setSharing(false);
  };
  const shareScreen = async () => {
    try {
      const ds = await navigator.mediaDevices.getDisplayMedia({ video: true, audio: false });
      const track = ds.getVideoTracks()[0];
      camTrackRef.current = localRef.current ? (localRef.current.getVideoTracks()[0] || null) : null;
      Object.values(peersRef.current).forEach(({ pc }) => { const s = videoSenderOf(pc); if (s) s.replaceTrack(track).catch(() => {}); });
      screenRef.current = ds; setScreenStream(ds); setSharing(true);
      track.onended = () => stopShare();
    } catch (e) { /* cancelled */ }
  };

  const toggleFs = () => {
    const el = overlayRef.current; if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.().catch(() => {});
    else document.exitFullscreen?.().catch(() => {});
  };

  const isHost = ["founder", "admin"].includes(user?.role);
  const muteAll = () => {
    const ids = Object.keys(peersRef.current);
    if (!ids.length) { toast.info("No one else to mute yet."); return; }
    ids.forEach((pid) => signal(pid, "control", { action: "mute" }));
    toast.success("Muted everyone in the call.");
  };

  useEffect(() => {
    const h = () => setIsFs(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", h);
    return () => document.removeEventListener("fullscreenchange", h);
  }, []);

  // fetch ICE/TURN config once ready
  useEffect(() => { if (user) refreshIce(); }, [user]); // eslint-disable-line react-hooks/exhaustive-deps

  // incoming poll (app-wide)
  useEffect(() => {
    if (!user) return;
    const t = setInterval(async () => {
      if (roomRef.current || incomingRef.current || connectingRef.current) return;
      try { const { data } = await api.get("/calls/incoming"); if (data && data.room_id) setIncoming(data); } catch (e) {}
    }, 3000);
    return () => clearInterval(t);
  }, [user]);

  // ring tone
  useEffect(() => {
    if (!incoming) return;
    let ctx, iv;
    try {
      ctx = new (window.AudioContext || window.webkitAudioContext)();
      const beep = () => {
        const o = ctx.createOscillator(); const g = ctx.createGain();
        o.type = "sine"; o.frequency.value = 520; o.connect(g); g.connect(ctx.destination);
        g.gain.setValueAtTime(0.0001, ctx.currentTime);
        g.gain.exponentialRampToValueAtTime(0.15, ctx.currentTime + 0.05);
        g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.5);
        o.start(); o.stop(ctx.currentTime + 0.55);
      };
      beep(); iv = setInterval(beep, 1500);
    } catch (e) {}
    return () => { if (iv) clearInterval(iv); if (ctx) ctx.close().catch(() => {}); };
  }, [incoming]);

  // duration
  useEffect(() => {
    if (!room) return;
    const t = setInterval(() => setSecs((s) => s + 1), 1000);
    return () => clearInterval(t);
  }, [room?.room_id]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { remoteStreamsRef.current = remoteStreams; }, [remoteStreams]);

  // active-speaker detection (Web Audio analyser per stream)
  useEffect(() => {
    if (!room) return;
    let ctx;
    try { ctx = new (window.AudioContext || window.webkitAudioContext)(); } catch (e) { return; }
    ctx.resume?.().catch(() => {});
    const nodes = {};
    const ensure = (id, stream) => {
      if (!stream || nodes[id] || !stream.getAudioTracks().length) return;
      try {
        const src = ctx.createMediaStreamSource(stream);
        const an = ctx.createAnalyser(); an.fftSize = 512;
        src.connect(an);
        nodes[id] = { an, data: new Uint8Array(an.frequencyBinCount), src };
      } catch (e) {}
    };
    const iv = setInterval(() => {
      ensure("self", localRef.current);
      Object.entries(remoteStreamsRef.current).forEach(([pid, r]) => ensure(pid, r.stream));
      const spk = {}; let selfSpk = false;
      Object.entries(nodes).forEach(([id, n]) => {
        n.an.getByteFrequencyData(n.data);
        let sum = 0; for (let i = 0; i < n.data.length; i++) sum += n.data[i];
        const on = (sum / n.data.length) > 15;
        if (id === "self") selfSpk = on; else spk[id] = on;
      });
      setSpeakingIds(spk); setLocalSpeaking(selfSpk);
    }, 250);
    return () => { clearInterval(iv); Object.values(nodes).forEach((n) => { try { n.src.disconnect(); } catch (e) {} }); ctx.close().catch(() => {}); };
  }, [room?.room_id]); // eslint-disable-line react-hooks/exhaustive-deps

  const mmss = `${String(Math.floor(secs / 60)).padStart(2, "0")}:${String(secs % 60).padStart(2, "0")}`;
  const ctxValue = useMemo(() => ({ startCall, startHuddle, joinRoom, inCall: !!room }), [startCall, startHuddle, joinRoom, room]);

  const tiles = Object.entries(remoteStreams);
  const cols = tiles.length <= 1 ? 1 : tiles.length <= 4 ? 2 : 3;
  const count = tiles.length + 1;
  const rxAudioTracks = tiles.flatMap(([, r]) => (r.stream ? r.stream.getAudioTracks() : []));
  const rxAudioLive = rxAudioTracks.filter((t) => !t.muted).length;
  const showSelf = sharing ? screenStream : (localStream && localStream.getVideoTracks().length > 0 ? localStream : null);

  return (
    <CallCtx.Provider value={ctxValue}>
      {children}

      {/* Incoming ringer */}
      {incoming && !room && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50" data-testid="incoming-call">
          <div className="w-80 rounded-2xl bg-white p-6 text-center shadow-2xl">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-navy text-xl font-bold text-gold animate-pulse">
              {initials(incoming.from_name)}
            </div>
            <div className="mt-3 flex items-center justify-center gap-1.5 text-xs font-medium text-emerald-600">
              <PhoneIncoming className="h-3.5 w-3.5" /> Incoming {incoming.media} call
            </div>
            <h3 className="mt-1 font-heading text-lg font-bold text-navy">{incoming.from_name}</h3>
            <p className="text-xs text-slate-400">in “{incoming.group_name}” · {incoming.count} in call</p>
            <div className="mt-5 flex justify-center gap-4">
              <Button onClick={decline} data-testid="call-decline" className="h-12 w-12 rounded-full bg-red-600 p-0 hover:bg-red-700"><PhoneOff className="h-5 w-5" /></Button>
              <Button onClick={accept} data-testid="call-accept" className="h-12 w-12 rounded-full bg-emerald-600 p-0 hover:bg-emerald-700"><Phone className="h-5 w-5" /></Button>
            </div>
          </div>
        </div>
      )}

      {/* Active call overlay */}
      {room && (
        <div
          ref={overlayRef}
          data-testid="active-call"
          className={isFs
            ? "fixed inset-0 z-[100] flex flex-col bg-navy text-white"
            : "fixed bottom-4 right-4 z-[100] w-96 overflow-hidden rounded-2xl bg-navy text-white shadow-2xl"}
        >
          <div className="flex items-center justify-between px-4 py-2 text-xs">
            <span className="flex items-center gap-1.5 font-semibold">
              <Users className="h-3.5 w-3.5" /> {room.group_name || "Huddle"} · {count} in call{sharing ? " · sharing" : ""}
            </span>
            <span className="text-white/70">{mmss}</span>
          </div>
          <div className="px-4 pb-1 text-[9px] text-white/40" data-testid="call-net-diag">net: {Object.keys(candTypes).join(", ") || "gathering…"}{Object.keys(candTypes).includes("relay") ? " ✓TURN" : ""} · turn-cfg:{turnCfg === null ? "…" : turnCfg} · rxA:{rxAudioLive}/{rxAudioTracks.length}</div>
          <div className={isFs ? "relative flex-1 min-h-0 overflow-hidden p-2" : "relative h-52 p-1.5"}>
            {tiles.length === 0 ? (
              <div className="flex h-full w-full flex-col items-center justify-center gap-2 rounded-lg bg-black/40">
                <div className="h-12 w-12 animate-pulse rounded-full bg-white/10" />
                <span className="text-xs text-white/60">Waiting for others to join…</span>
              </div>
            ) : (
              <div className="grid h-full w-full gap-1.5" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridAutoRows: "1fr" }}>
                {tiles.map(([pid, r]) => (<RemoteTile key={pid} stream={r.stream} videoOn={r.videoOn} name={r.name} speaking={!!speakingIds[pid]} state={peerStates[pid]} />))}
              </div>
            )}
            {showSelf && (
              <div className={`absolute overflow-hidden rounded-lg border transition-shadow ${localSpeaking ? "border-emerald-400 ring-2 ring-emerald-400" : "border-white/20"} ${isFs ? "bottom-4 right-4 h-40 w-56" : "bottom-2 right-2 h-20 w-28"}`}>
                <Stream stream={showSelf} muted className={sharing ? "h-full w-full bg-black object-contain" : "h-full w-full object-cover"} />
              </div>
            )}
          </div>
          <div className="flex items-center justify-center gap-3 py-3">
            <button onClick={toggleMute} data-testid="call-mute" className={`flex h-10 w-10 items-center justify-center rounded-full ${muted ? "bg-white/20" : "bg-white/10"} hover:bg-white/25`}>
              {muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
            </button>
            {room.media === "video" && (
              <button onClick={toggleCam} data-testid="call-cam" className={`flex h-10 w-10 items-center justify-center rounded-full ${camOff ? "bg-white/20" : "bg-white/10"} hover:bg-white/25`}>
                {camOff ? <VideoOff className="h-4 w-4" /> : <Video className="h-4 w-4" />}
              </button>
            )}
            <button onClick={sharing ? stopShare : shareScreen} data-testid="call-share" title={sharing ? "Stop sharing" : "Share screen"} className={`flex h-10 w-10 items-center justify-center rounded-full ${sharing ? "bg-gold text-navy" : "bg-white/10"} hover:bg-white/25`}>
              {sharing ? <ScreenShareOff className="h-4 w-4" /> : <ScreenShare className="h-4 w-4" />}
            </button>
            <button onClick={toggleFs} data-testid="call-fullscreen" title={isFs ? "Exit full screen" : "Full screen"} className="flex h-10 w-10 items-center justify-center rounded-full bg-white/10 hover:bg-white/25">
              {isFs ? <Minimize className="h-4 w-4" /> : <Maximize className="h-4 w-4" />}
            </button>
            {isHost && (
              <button onClick={muteAll} data-testid="call-mute-all" title="Mute everyone" className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-500/90 text-navy hover:bg-amber-500">
                <MicOff className="h-4 w-4" />
              </button>
            )}
            <button onClick={hangup} data-testid="call-hangup" className="flex h-11 w-11 items-center justify-center rounded-full bg-red-600 hover:bg-red-700">
              <PhoneOff className="h-5 w-5" />
            </button>
          </div>
        </div>
      )}
    </CallCtx.Provider>
  );
};
